import { useState, useEffect } from 'react';

export const useHighContrastMode = () => {
  // Lazy initializers read localStorage + matchMedia once — no setState inside effect
  const [isHighContrast, setIsHighContrast] = useState(() => {
    const saved = localStorage.getItem('high-contrast-mode');
    return saved !== null
      ? saved === 'true'
      : window.matchMedia('(prefers-contrast: high)').matches;
  });
  const [userPreference, setUserPreference] = useState<boolean | null>(() => {
    const saved = localStorage.getItem('high-contrast-mode');
    return saved !== null ? saved === 'true' : null;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-contrast: high)');
    const handler = (e: MediaQueryListEvent) => {
      if (userPreference === null) {
        setIsHighContrast(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [userPreference]);

  useEffect(() => {
    // Apply high contrast class to document
    if (isHighContrast) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
  }, [isHighContrast]);

  const toggleHighContrast = () => {
    const newValue = !isHighContrast;
    setIsHighContrast(newValue);
    setUserPreference(newValue);
    localStorage.setItem('high-contrast-mode', String(newValue));
  };

  const resetToSystemPreference = () => {
    localStorage.removeItem('high-contrast-mode');
    const systemPreference = window.matchMedia('(prefers-contrast: high)').matches;
    setIsHighContrast(systemPreference);
    setUserPreference(null);
  };

  return { 
    isHighContrast, 
    toggleHighContrast, 
    resetToSystemPreference,
    hasUserPreference: userPreference !== null
  };
};
